import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
  inject,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CustomDatePipe } from 'src/app/pipe/date-format.pipe';
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DisplayFileComponent } from 'src/app/shared/display-file/display-file.component';
import { authService } from 'src/app/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { TruncateHtml } from 'src/app/pipe/truncateHtml.pipe';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslationService } from 'src/app/services/translante.service';
import { TranslationDialogComponent } from 'src/app/shared/html-editor/link/translante';

export enum emailType {
  systemMail = 'System mail',
  gmail = 'gmail',
}

@Component({
  selector: 'app-chat-item',
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    CustomDatePipe,
    MatMenuModule,
    TruncateHtml,
    DisplayFileComponent,
    MaterialModule,
  ],
  templateUrl: './chat-item.component.html',
  styleUrls: ['./chat-item.component.scss'],
})
export class ChatItemComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() chat: any;
  @Input() leadName: any;
  @Input() focusMode = false;
  @Input() repName: any;
  @Input() isEmailSync: any;
  @Input() removeButton = false;
  @Input() isSendingMessage = false;
  @Input() currentUser: any;
  @Input() isLast = false;
  @Output() chatReply = new EventEmitter<void>();
  @Output() infoChat = new EventEmitter<void>();

  private dialog = inject(MatDialog);
  private translationService = inject(TranslationService);
  private snackBar = inject(MatSnackBar);

  backendUrl = environment.apiUrl;
  allowReply = false;

  @ViewChild('contentElement') contentElement!: ElementRef;

  /** ──────────────────────────────
   *   UI / content-state flags (EXISTING)
   * ────────────────────────────── */
  isExpanded   = false;
  isTruncated  = false;
  isHtmlEmail  = false;
  hasTemporaryFailure = false;
  temporaryFailureMessage = '';
  isTranslating = false;
  canTranslate  = true;

  /** ──────────────────────────────
   *   NEW: Pending message flags
   * ────────────────────────────── */
  pendingAttachmentCount = 0;

  /** ──────────────────────────────
   *   Content buffers (EXISTING)
   * ────────────────────────────── */
  rawContent       = '';
  originalContent  = '';
  currentLanguage  = '';
  sanitizedContent!: SafeHtml;
  truncatedContent!: SafeHtml;
  private contentProcessed = false;

  /** ──────────────────────────────
   *   Signature helpers (EXISTING)
   * ────────────────────────────── */
  private static readonly SIGNATURE_SELECTOR =
    '[data-signature="true"], #signature-wrapper, .email-signature-content';

  /** Remove the signature element and return body / signature separately */
  private splitSignature(html: string): { bodyHtml: string; signatureHtml: string } {
    const container = document.createElement('div');
    container.innerHTML = html;

    const sigEl = container.querySelector(
      ChatItemComponent.SIGNATURE_SELECTOR
    ) as HTMLElement;

    const signatureHtml = sigEl ? sigEl.outerHTML : '';
    if (sigEl) sigEl.remove();

    return { bodyHtml: container.innerHTML, signatureHtml };
  }

  /** Strip tags / &nbsp; so we feed plain text to language detection */
  private htmlToPlainText(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html.replace(/&nbsp;/g, ' ');
    return div.textContent || '';
  }

  /* ───────── life-cycle ───────── */

  constructor(private sanitizer: DomSanitizer, private authService: authService) {}

  ngOnInit() {
    // Handle permission checks for reply functionality
    if (!this.focusMode) {
      if (this.chat.direction === 'received') {
        this.allowReply = this.isEmailSync
          ? this.chat.typeEmail === emailType.gmail
          : this.chat.typeEmail === emailType.systemMail &&
            (this.isEmailSync
              ? this.currentUser.gmail_integration === this.chat.to
              : this.currentUser.email_company === this.chat.to);
      } else {
        this.allowReply = this.isEmailSync
          ? this.chat.typeEmail === emailType.gmail
          : this.chat.typeEmail === emailType.systemMail &&
            (this.isEmailSync
              ? this.currentUser.gmail_integration === this.chat.from
              : this.currentUser.email_company === this.chat.from);
      }
    }
    
    // Initialize content
    this.resetAndProcessContent();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.checkContentHeight(), 100);
  }
  
  ngOnChanges(changes: any) {
    if (changes.chat && !changes.chat.firstChange) {
      this.resetAndProcessContent();
    }
  }
  
  // New method to reset and process content in one place
  private resetAndProcessContent(): void {
    // Reset state
    this.contentProcessed = false;
    this.rawContent = this.chat?.content || this.chat?.rawContent || '';
    this.originalContent = this.chat?.content || this.rawContent;
    this.currentLanguage = 'original';
    
    this.hasTemporaryFailure =
      this.isPendingMessage
        ? !!this.chat?.temporary_message
        : !!this.chat?.temporary_failure && !!this.chat?.temporary_message;

    this.temporaryFailureMessage =
      this.chat?.temporary_message || 'Message temporarily queued';

    this.pendingAttachmentCount = this.chat?.attachment_count || 0;

    // Process content
    if (this.rawContent) {
      this.isHtmlEmail = this.checkIfHtmlEmail(this.rawContent);
      this.canTranslate = !this.isHtmlEmail;
      this.processContent();
    }
  }

  get isPendingMessage(): boolean {
    const chat = this.chat;
    if (!chat) return false;

    return !!(
      chat.temporary_failure ||
      chat.unique_id ||
      chat.isPending ||
      (chat.id && chat.id.toString().startsWith('pending-'))
    );
  }

  get showPendingProgress(): boolean {
    // Spinner visible only while pending AND not failed
    return this.isPendingMessage && !this.chat?.isError;
  }

  /* ───────── translation (EXISTING - NO CHANGES) ───────── */

  async handleTranslate(): Promise<void> {
    if (!this.rawContent || this.isPendingMessage) return;

    /* 0. Toggle back to original */
    if (this.currentLanguage !== 'original') {
      this.rawContent       = this.originalContent;
      this.currentLanguage  = 'original';
      this.contentProcessed = false;
      this.processContent();
      this.snackBar.open('Reverted to original language', 'Close', { duration: 2000 });
      return;
    }

    /* 1. Always decouple signature (works for any content) */
    const { bodyHtml, signatureHtml } = this.splitSignature(this.originalContent);

    /* 2. Extract plain text for detection / translation */
    const textToTranslate = this.isHtmlEmail
      ? this.htmlToPlainText(bodyHtml)
      : this.htmlToPlainText(bodyHtml);   // same path for non-HTML snippets

    if (!textToTranslate.trim()) {
      this.snackBar.open('No text to translate', 'Close', { duration: 2000 });
      return;
    }

    /* 3. Detect language → dialog for target language */
    this.translationService.detectLanguage(textToTranslate).subscribe({
      next: (detectResult) => {
        const dlg = this.dialog.open(TranslationDialogComponent, {
          width: '400px',
          data : {
            currentText      : textToTranslate,
            detectedLanguage : detectResult.detectedLanguage,
          },
        });

        dlg.afterClosed().subscribe((result) => {
          if (!result) return;

          /* 4. Translate body */
          this.isTranslating = true;
          const loading = this.snackBar.open('Translating message...', '', { duration: 0 });

          this.translationService.translateText(textToTranslate, result.targetLanguage).subscribe({
            next: (tr) => {
              loading.dismiss();
              this.isTranslating = false;

              /* 5. Re-assemble final HTML / plain text + untouched signature */
              if (this.isHtmlEmail) {
                const temp = document.createElement('div');
                temp.innerHTML = bodyHtml;                 // keep structure
                temp.innerHTML = tr.translatedText;        // swap text
                this.rawContent = temp.innerHTML + signatureHtml;
              } else {
                this.rawContent = tr.translatedText + signatureHtml;
              }

              /* 6. Refresh state */
              this.currentLanguage  = result.targetLanguage;
              this.contentProcessed = false;
              this.processContent();

              const src = tr.sourceLanguage || detectResult.detectedLanguage || 'auto';
              const dst = tr.targetLanguage || result.targetLanguage;
              this.snackBar.open(
                `Translated from ${this.getLanguageName(src)} to ${this.getLanguageName(dst)}`,
                'Close',
                { duration: 3000 }
              );
            },
            error: (err) => {
              loading.dismiss();
              this.isTranslating = false;
              this.snackBar.open('Translation failed: ' + err.message, 'Close', { duration: 4000 });
            },
          });
        });
      },
      error: () => this.snackBar.open('Language detection failed', 'Close', { duration: 3000 }),
    });
  }

  /* ───────── helpers & leftovers (EXISTING - NO CHANGES) ───────── */

  getLanguageName(code: string | undefined | null): string {
    if (!code) return 'Unknown';
    const map: Record<string, string> = {
      en: 'English',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      pt: 'Portuguese',
      zh: 'Chinese',
      ja: 'Japanese',
      ko: 'Korean',
      ar: 'Arabic',
      hi: 'Hindi',
      ru: 'Russian',
    };
    return map[code] || code.toUpperCase();
  }

  private preprocessHtmlContent(content: string): string {
    if (!content) return content;
    let processed = content.replace(/(&nbsp;){2,}/g, ' ');
    processed = processed.replace(/(\w)&nbsp;(\w)/g, '$1 $2');
    return processed;
  }

  private processContent(): void {
    if (this.contentProcessed) return;

    const processedContent = this.preprocessHtmlContent(this.rawContent);

    if (this.isHtmlEmail) {
      this.handleHtmlEmail(processedContent);
    } else {
      this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(processedContent);
      const truncated =
        processedContent.length > 300
          ? processedContent.substring(0, 300) + '...'
          : processedContent;
      this.truncatedContent = this.sanitizer.bypassSecurityTrustHtml(truncated);
    }

    this.contentProcessed = true;
  }

  private handleHtmlEmail(html: string): void {
    const srcdoc = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <base target="_blank">
          <style>
            body {
              margin: 0;
              padding: 16px;
              background-color: #ffffff;
              color: #333;
            }
          </style>
        </head>
        <body>${html}</body>
      </html>`.trim();
  
    // Check if dark mode is active
    const isDarkMode = document.body.classList.contains('dark-theme');
    
    const darkModeFilter = isDarkMode 
      ? 'filter: invert(0.9) hue-rotate(180deg);' 
      : '';
  
    const iframeContent = `
      <div class="email-container">
        <iframe class="email-frame"
                srcdoc='${srcdoc.replace(/'/g, "&#39;")}'
                frameborder="0"
                width="100%"
                style="min-height:100px;width:100%;border:none;overflow:hidden;border-radius:8px;${darkModeFilter}"
                sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                onload="
                  try {
                    const h = this.contentDocument && this.contentDocument.body
                      ? this.contentDocument.body.scrollHeight
                      : 300;
                    this.style.height = Math.max(300, h + 20) + 'px';
                  } catch(e) { console.error(e); }
                ">
        </iframe>
      </div>`;
  
    this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(iframeContent);
    this.truncatedContent = this.sanitizedContent;
  }
  

  private checkContentHeight(): void {
    if (this.contentElement) {
      const el = this.contentElement.nativeElement as HTMLElement;
      this.isTruncated = el.scrollHeight > 48; // ≈3 lines @16 px
    }
  }

  checkIfHtmlEmail(content: string): boolean {
    if (!content) return false;
    return (
      content.includes('<!DOCTYPE html>') ||
      content.includes('<html') ||
      (content.includes('<body') && content.includes('<head'))
    );
  }

  private escapeHtml(html: string): string {
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // UPDATED: toggleContent with pending check
  async toggleContent(event?: MouseEvent) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Don't allow expand for pending messages
    if (this.isPendingMessage) {
      return;
    }

    // Normal expand logic for regular messages
    try {
      if (this.chat.attachments?.length) {
        const toSign = this.chat.attachments.filter(
          (a) => ['image', 'video', 'audio'].includes(a.type) && !a.signUrl
        );
        if (toSign.length) {
          const obj = { keys: toSign, expSeconds: 3600, type: 'messages' };
          const res: any = await firstValueFrom(this.authService.getSignUrls(obj));
          this.chat.attachments = this.chat.attachments.map((a) => {
            const s = res.signedUrls.find((x) => x.cloud_key === a.cloud_key);
            return s ? { ...a, signUrl: s.signUrl } : a;
          });
        }
      }

      this.isExpanded = !this.isExpanded;

      if (this.isExpanded && this.isHtmlEmail) {
        setTimeout(() => {
          document.querySelectorAll('.email-frame').forEach((iframe: any) => {
            try {
              const h = iframe.contentDocument.body.scrollHeight;
              iframe.style.height = Math.max(300, h + 20) + 'px';
            } catch (e) {
              console.error('Frame resize error:', e);
            }
          });
        }, 100);
      }
    } catch (e) {
      console.error('Error toggling content:', e);
    }
  }

  async fileClick(key: string) {
    const response: any = await firstValueFrom(
      this.authService.getSignUrl(`messages/${key}`, '500')
    );
    window.open(response.signUrl, '_blank');
  }

  replyEmail(evt: any) {
    this.chatReply.emit(evt);
  }

  seeInfo(evt: any) {
    this.infoChat.emit(evt);
  }

// WITH THIS:
getSenderInitials(): string {
  const name = this.getSenderFullName();
  const parts = name.split(' ').filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return Array.from(parts[0]).slice(0, 2).join('');
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// AFTER
private readonly APPLE_REACTION_MAP: Record<string, string> = {
  loved:      '❤️',
  liked:      '👍',
  disliked:   '👎',
  laughed:    '😂',
  emphasized: '‼️',
  questioned: '❓',
};

getReactionEmoji(reactionType: string): string {
  return this.APPLE_REACTION_MAP[reactionType] ?? reactionType;
}

getReactionTooltip(reaction: any): string {
  const platform = reaction.platform === 'apple' ? 'iPhone' : 'Android';
  const type = reaction.reaction_type.charAt(0).toUpperCase() + reaction.reaction_type.slice(1);
  return `${type} via ${platform}`;
}

getSenderFullName(): string {
  if (this.chat.direction === 'sent') {
    return this.chat.createdBy
      ? `${this.chat.createdBy.name || ''} ${this.chat.createdBy.lastName || ''}`.trim()
      : 'System';
  } else {
    return this.leadName || 'Lead';
  }
}
}